import { logError } from './errorHandling';

/**
 * Centralized error handling utility for async operations
 * @param operation Async function to execute
 * @param context Optional context for error logging
 * @param options Configuration for error handling
 * @returns Promise resolving to the operation's result
 */
export async function withErrorHandling<T>(
  operation: () => Promise<T>,
  context?: Record<string, any>,
  options: {
    rethrow?: boolean;
    silent?: boolean;
  } = {}
): Promise<T> {
  const { rethrow = true, silent = false } = options;

  try {
    return await operation();
  } catch (error: any) {
    // Log the error if not in silent mode
    if (!silent) {
      logError(error, {
        ...context,
        operationName: operation.name || 'anonymous'
      });
    }

    // Create a standardized error response
    const errorResponse = {
      message: error.message || 'An unexpected error occurred',
      code: error.code || 'UNKNOWN_ERROR',
      context: {
        ...context,
        timestamp: new Date().toISOString()
      }
    };

    // Rethrow if configured to do so
    if (rethrow) {
      const enhancedError = new Error(errorResponse.message);
      Object.assign(enhancedError, errorResponse);
      throw enhancedError;
    }

    // Return undefined if not rethrowing
    return undefined as T;
  }
}

/**
 * Create a typed error handler for specific use cases
 * @param errorType Custom error type or category
 * @returns Wrapped error handling function
 */
export function createErrorHandler(errorType: string) {
  return async <T>(
    operation: () => Promise<T>, 
    context?: Record<string, any>
  ) => withErrorHandling(operation, { ...context, errorType }, { rethrow: true });
}